import {OnDestroy} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/takeUntil';
import {AdminService} from './admin.service';
import {isStandardVersionConflict, savedStandardVersion} from './standard-version';
import {StandardFiles, StandardFileSelection, STANDARD_FILE_METADATA} from '../models/standard-files.model';
import {Blank} from '../models/blank.model';
import {NewBlank} from '../models/new-blank.model';
import {ConfirmationPopupComponent} from '../content-page/views/view-group/popups/confirmation-popup/confirmation-popup.component';

const FILE_DELETE_CONFIRMATION = 'STANDARD_FILE_DELETE_CONFIRM';
const FILE_DELETE_ACCEPTED = 'ok';
const BLANK_DELETE_CONFIRMATION = 'DYWTDTBF';
const HTTP_OK = 200;
const NO_FILE_ERROR = '';
const FILE_ERROR = {conflict: 'STANDARD_VERSION_CONFLICT_MESSAGE', failed: 'STANDARD_FILE_ACTION_FAILED'};
interface FileAction {
  source: StandardFiles;
  snapshot: StandardFiles;
  originalVersion: number;
  selection: StandardFileSelection;
  accept: () => void;
}

export class StandardFileActions implements OnDestroy {
  pending = false;
  requiresReload = false;
  errorKey = NO_FILE_ERROR;
  private destroyed = false;
  private cancelled$ = new Subject<void>();

  constructor(private adminService: AdminService, private modalService: NgbModal) {}

  remove(source: StandardFiles, selection: StandardFileSelection, accept: () => void): void {
    if (!selection.link && !selection.fileId) { return; }
    const action = this.begin(source, selection, accept);
    if (!action) { return; }
    this.confirm(FILE_DELETE_CONFIRMATION, () => this.sendRemoval(action));
  }

  removeBlank(source: StandardFiles, blank: Blank, accept: () => void): void {
    if (!blank || !blank.fileId) { return; }
    const captured = Object.assign({}, blank);
    const action = this.begin(source, null, accept);
    if (!action) { return; }
    this.confirm(BLANK_DELETE_CONFIRMATION, () => {
      this.adminService.deleteBlankFromStandard(captured, action.snapshot).takeUntil(this.cancelled$).subscribe(
        response => this.acceptBlankRemoval(action, captured.fileId, response), error => this.reject(error)
      );
    });
  }

  uploadBlanks(source: StandardFiles, drafts: NewBlank[], accept: () => void): void {
    if (!drafts.length) { return; }
    const action = this.begin(source, null, accept);
    if (!action) { return; }
    this.adminService.uploadBlank(action.snapshot, drafts).takeUntil(this.cancelled$).subscribe(
      (response: HttpResponse<Blank[]>) => this.acceptBlanks(action, response), error => this.reject(error)
    );
  }

  upload(source: StandardFiles, selection: StandardFileSelection, accept: () => void): void {
    if (!selection.file) { return; }
    const action = this.begin(source, selection, accept);
    if (!action) { return; }
    const data = new FormData();
    data.set(selection.type, selection.file, selection.file.name);
    this.adminService.uploadFiles(action.snapshot, data).concatMap(response => {
      if (savedStandardVersion(response) == null) { throw new Error(FILE_ERROR.failed); }
      return this.adminService.getStandard(action.snapshot.id);
    }).takeUntil(this.cancelled$).subscribe(
      response => this.acceptUpload(action, response), error => this.reject(error)
    );
  }

  reset(): void {
    this.cancelled$.next();
    this.pending = this.requiresReload = false;
    this.errorKey = NO_FILE_ERROR;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelled$.next();
    this.cancelled$.complete();
  }

  private begin(source: StandardFiles, selection: StandardFileSelection, accept: () => void): FileAction {
    if (this.pending || this.requiresReload || this.destroyed || !source
      || (selection && !STANDARD_FILE_METADATA[selection.type])) { return null; }
    this.pending = true;
    this.errorKey = NO_FILE_ERROR;
    return {source: source, snapshot: Object.assign({}, source), originalVersion: source.version,
      selection: Object.assign({}, selection), accept: accept};
  }

  private confirm(message: string, send: () => void): void {
    const modal = this.modalService.open(ConfirmationPopupComponent);
    modal.componentInstance.massage = message;
    modal.result.then(result => {
      if (this.destroyed) { return; }
      if (result === FILE_DELETE_ACCEPTED) { send(); }
      else { this.pending = false; }
    }, () => { this.pending = false; });
  }

  private sendRemoval(action: FileAction): void {
    this.removalRequest(action).takeUntil(this.cancelled$).subscribe(
      response => this.acceptRemoval(action, response), error => this.reject(error)
    );
  }

  private removalRequest(action: FileAction): Observable<HttpResponse<Object>> {
    const selected = action.selection;
    const removeFile = () => this.adminService.deleteFileFromStandard(selected.fileId, selected.type, action.snapshot);
    if (!selected.link) { return removeFile(); }
    const request = this.adminService.deleteTempDocxFileManually(selected.link, action.snapshot);
    return selected.fileId ? request.concatMap(response => {
      if (savedStandardVersion(response) == null) { throw new Error(FILE_ERROR.failed); }
      return removeFile();
    }) : request;
  }

  private acceptRemoval(action: FileAction, response: HttpResponse<Object>): void {
    const version = savedStandardVersion(response);
    if (response.status !== HTTP_OK || version == null) { this.fail(FILE_ERROR.failed); return; }
    if (!this.acceptVersion(action, version)) { return; }
    const fields = STANDARD_FILE_METADATA[action.selection.type];
    action.source[fields.field] = null;
    if (fields.link) { action.source[fields.link] = null; }
    action.accept();
  }

  private acceptUpload(action: FileAction, response: HttpResponse<Object>): void {
    const fresh = response.body as StandardFiles;
    const version = savedStandardVersion(response);
    if (response.status !== HTTP_OK || !fresh || fresh.id !== action.snapshot.id || version == null) { this.fail(FILE_ERROR.failed); return; }
    if (version !== action.snapshot.version) { this.fail(FILE_ERROR.conflict); return; }
    if (!this.acceptVersion(action, version)) { return; }
    const fields = STANDARD_FILE_METADATA[action.selection.type];
    [fields.field].concat(fields.related).forEach(field => action.source[field] = fresh[field]);
    action.accept();
  }

  private acceptVersion(action: FileAction, version: number): boolean {
    if (action.source.version !== action.originalVersion) { this.fail(FILE_ERROR.conflict); return false; }
    action.source.version = version;
    this.pending = false;
    return true;
  }

  private acceptBlankRemoval(action: FileAction, blankId: string, response: HttpResponse<Object>): void {
    const version = savedStandardVersion(response);
    if (response.status !== HTTP_OK || version == null) { this.fail(FILE_ERROR.failed); return; }
    if (!this.acceptVersion(action, version)) { return; }
    action.source.blanks = (action.source.blanks || []).filter(blank => blank.fileId !== blankId);
    action.accept();
  }

  private acceptBlanks(action: FileAction, response: HttpResponse<Blank[]>): void {
    const version = savedStandardVersion(response);
    if (response.status !== HTTP_OK || version == null || !Array.isArray(response.body)
      || !response.body.length) { this.fail(FILE_ERROR.failed); return; }
    if (!this.acceptVersion(action, version)) { return; }
    action.source.blanks = (action.source.blanks || []).concat(response.body);
    action.accept();
  }

  private reject(error: HttpErrorResponse): void {
    this.fail(isStandardVersionConflict(error) ? FILE_ERROR.conflict : FILE_ERROR.failed);
  }

  private fail(errorKey: string): void {
    this.pending = false;
    this.requiresReload = true;
    this.errorKey = errorKey;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/standard-file-actions.ts